import axios from 'axios'
import { message } from 'ant-design-vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import MainLayout from '../MainLayout.vue'
import { useActiveModule, useModuleRoutes, useModules } from '../moduleAdapter'
import { useI18n } from '../composables/useI18n'
import { useAuthStore } from '../stores/auth'
import { useFeatureStore } from '../stores/feature'

/** Default landing target: the active module's home, falling back to the first module */
const defaultPath = () => useActiveModule()?.homePath || useModules()[0]?.homePath || '/login'

// base 自带的管理员页面（仅管理员可访问）
const baseAdminRoutes: RouteRecordRaw[] = [
  {
    path: '/base/user',
    name: 'AdminUser',
    meta: { titleKey: 'base.adminUser.title' },
    component: () => import('../views/AdminUser.vue'),
  },
  {
    path: '/base/register',
    name: 'AdminRegister',
    meta: { titleKey: 'base.adminRegister.title' },
    component: () => import('../views/AdminRegister.vue'),
  },
  {
    path: '/base/agreement',
    redirect: '/base/register',
  },
  {
    path: '/base/pay',
    name: 'AdminPay',
    meta: { titleKey: 'base.adminPay.title' },
    component: () => import('../views/AdminPay.vue'),
  },
  {
    path: '/base/package',
    name: 'AdminPackage',
    meta: { titleKey: 'base.adminPackage.title' },
    component: () => import('../views/AdminPackage.vue'),
  },
  {
    path: '/base/order',
    name: 'AdminOrder',
    meta: { titleKey: 'base.adminOrder.title' },
    component: () => import('../views/AdminOrder.vue'),
  },
  {
    path: '/base/setting',
    name: 'AdminSetting',
    meta: { titleKey: 'base.adminSetting.title' },
    component: () => import('../views/AdminSetting.vue'),
  },
  {
    path: '/base/notify',
    name: 'AdminNotify',
    meta: { titleKey: 'base.adminNotify.title' },
    component: () => import('../views/AdminNotify.vue'),
  },
  {
    path: '/base/attr',
    name: 'AdminAttr',
    meta: { titleKey: 'base.adminAttr.title' },
    component: () => import('../views/AdminAttr.vue'),
  },
]

const routes: RouteRecordRaw[] = [
  {
    // Public landing page: exact '/' match wins over the layout record below.
    // The module intro page lives only here; signed-in visitors see it with a "open workspace" CTA.
    path: '/',
    name: 'Landing',
    component: () => import('../views/ModuleLanding.vue'),
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
  },
  {
    // 注册视图复用登录页组件，仅地址不同（/register）
    path: '/register',
    name: 'Register',
    component: () => import('../views/Login.vue'),
  },
  {
    path: '/setup',
    name: 'Setup',
    meta: { titleKey: 'base.setup.title' },
    component: () => import('../views/Setup.vue'),
  },
  {
    path: '/reset',
    name: 'ResetPassword',
    meta: { titleKey: 'base.resetPassword.title' },
    component: () => import('../views/ResetPassword.vue'),
  },
  {
    path: '/',
    component: MainLayout,
    // No redirect here: '/' is resolved by the guard (guest -> landing, signed-in -> module home)
    children: [...useModuleRoutes(), ...baseAdminRoutes],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    meta: { titleKey: 'base.notFound.title' },
    component: () => import('../views/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory((window as any).__UI_PATH__ || '/'),
  routes,
})

let _initializedCache: boolean | null = null

async function checkSystemInitialized(): Promise<boolean> {
  if (_initializedCache !== null) return _initializedCache
  try {
    const resp = await axios.post('/api/system/initStatus')
    _initializedCache = resp.data?.data?.initialized === true
    return _initializedCache
  } catch {
    return true
  }
}

// 完成初始化后清除缓存，让下次路由检查重新获取状态
export function resetInitializedCache() {
  _initializedCache = null
}

// 仅管理员可访问的路由前缀：各模块在 ModuleConfig.adminPaths 中声明 + base 管理页
const adminOnlyPaths = [
  ...useModules().flatMap((m) => m.adminPaths ?? []),
  '/base/user',
  '/base/register',
  '/base/pay',
  '/base/package',
  '/base/order',
  '/base/setting',
  '/base/notify',
  '/base/attr',
]

// 未开启点数功能时需要拦截的点数相关路由
const creditOnlyPaths = ['/base/package', '/base/order']

// 登录前可访问的鉴权页面：已登录时跳首页，未登录时允许停留
const publicAuthPaths = ['/login', '/register']

router.beforeEach(async (to, _from) => {
  // setup 页面：检查是否已初始化，已初始化则跳到登录
  if (to.path === '/setup') {
    const initialized = await checkSystemInitialized()
    if (initialized) return '/login'
    return true
  }

  // 重置密码页面：公开访问
  if (to.path === '/reset') return true

  // 所有非 setup 页面：先检查系统是否已初始化
  const initialized = await checkSystemInitialized()
  if (!initialized) return '/setup'

  const auth = useAuthStore()
  if (!auth.isInitialized) {
    await auth.autoLogin()
  }

  // Public landing page: modules with a landing always show it at '/', others fall back to login / home
  if (to.path === '/') {
    if (useActiveModule()?.landing) return true
    return auth.isAuthenticated ? defaultPath() : '/login'
  }

  const isPublicAuthPath = publicAuthPaths.includes(to.path)
  if (isPublicAuthPath) return auth.isAuthenticated ? defaultPath() : true
  if (!auth.isAuthenticated) return '/login'

  // 加载功能开关（点数等），供菜单 / 页面显隐
  const feature = useFeatureStore()
  await feature.ensureLoaded()

  // 未开启点数功能时，拦截点数相关页面
  if (!feature.creditEnabled && creditOnlyPaths.some((p) => to.path.startsWith(p))) {
    return defaultPath()
  }

  // 管理员专属页面，普通用户提示无权限并跳转到首页
  if (adminOnlyPaths.some((p) => to.path.startsWith(p)) && !auth.isAdmin) {
    const { t } = useI18n()
    message.warning(t('common.noPermission'))
    return defaultPath()
  }
})

export default router
